/**
 * MapProvider registry (ODE-P07-T02, REQ-084 / OD-009).
 *
 * Resolves the configured `MapProvider` implementation into a ready-to-use
 * handle. This is the ONLY place concrete adapters are constructed:
 *
 *   - `fake`   → FakeMapAdapter (hermetic; used by tests + dev/demo)
 *   - `google` → GoogleMapsJsMapAdapter, constructed ONLY when a runtime
 *                API key is present (never committed; script loads lazily
 *                at mount through the loader seam)
 *   - missing key / unknown provider → `misconfigured` handle (the UI
 *                renders the explicit REQ-090 state; never a crash)
 *
 * Traces to: ODE-PRD-REQ-084, ODE-PRD-REQ-090, ODE-OD-009.
 */

import { FakeMapAdapter } from './fake-adapter.js';
import { GoogleMapsJsMapAdapter, type GoogleMapsLoader } from './google-adapter.js';
import type { MapProvider, MapProviderResolution } from './map-provider.js';

/** Resolved provider handle for the UI (either a provider, or a reason). */
export type MapProviderHandle =
  | { readonly kind: 'provider'; readonly provider: MapProvider }
  | { readonly kind: 'misconfigured'; readonly reason: string };

export interface CreateMapProviderOptions {
  /** Loader seam for the Google adapter (tests inject a fake loader). */
  readonly loader?: GoogleMapsLoader;
  /** Error sink for runtime adapter failures (UI renders the error state). */
  readonly onError?: (message: string) => void;
}

/**
 * Builds the provider handle from a resolution result. Pure factory — no
 * globals, no side effects; constructing the Google adapter does NOT load
 * the Maps script (that happens at `mount` time with a present key).
 */
export function createMapProviderHandle(
  resolution: MapProviderResolution,
  options: CreateMapProviderOptions = {},
): MapProviderHandle {
  switch (resolution.kind) {
    case 'fake':
      return { kind: 'provider', provider: new FakeMapAdapter() };
    case 'google': {
      const adapterOptions: {
        loader?: GoogleMapsLoader;
        onError?: (message: string) => void;
      } = {};
      if (options.loader !== undefined) adapterOptions.loader = options.loader;
      if (options.onError !== undefined) adapterOptions.onError = options.onError;
      return {
        kind: 'provider',
        provider: new GoogleMapsJsMapAdapter(resolution.apiKey, adapterOptions),
      };
    }
    case 'misconfigured':
      return { kind: 'misconfigured', reason: resolution.reason };
  }
}
